import React from 'react';
import { BoxIcon } from '../icons/Icons';
import { getImageUrl } from '../../config/api';

/**
 * ============================================
 * Bélanger-Style Product Card
 * ============================================
 * Exact match to Bélanger's catalog design
 */

const ProductCard = ({ product, onClick }) => {
    // Get unique color variants (limit to first 3)
    const colorVariants = product.variants?.slice(0, 3) || [];
    
    // Determine badge type based on product properties
    const getBadge = () => {
        if (product.isFeatured) return { label: 'PRO', color: 'bg-pink-50 text-pink-900 border border-pink-100' };
        if (product.isNew) return { label: 'NEW', color: 'bg-gray-800 text-white border border-gray-800' };
        return { label: 'ESSENTIAL', color: 'bg-pink-50 text-pink-900 border border-pink-100' };
    };

    const badge = getBadge();
    
    // Generate product code (use SKU or create from name)
    const productCode = product.sku || product.name.substring(0, 10).toUpperCase().replace(/\s/g, '');
    
    // Get image URL with proper base URL
    const imageUrl = getImageUrl(product.mainImage) || getImageUrl(product.image) || product.images?.[0] || product.imageUrl;

    return (
        <article 
            onClick={() => onClick(product)}
            className="group bg-white overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-xl"
        >
            {/* Image Container */}
            <div className="relative aspect-[3/4] overflow-hidden bg-white">
                <div className="absolute inset-0 flex items-center justify-center p-8">
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt={product.name}
                            className="max-w-full max-h-full object-contain"
                            loading="lazy"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-20 h-20 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg></div>';
                            }}
                        />
                    ) : (
                        <BoxIcon className="w-20 h-20 text-gray-300" />
                    )}
                </div>
                
                {/* Color variant circles - top left */}
                {colorVariants.length > 0 && (
                    <div className="absolute top-4 left-4 flex gap-1">
                        {colorVariants.map((variant, idx) => (
                            <div 
                                key={idx}
                                className="w-7 h-7 rounded-full border-2 border-white shadow-sm"
                                style={{ 
                                    backgroundColor: variant.color || ['#2c2c2c', '#c0c0c0', '#d4af37'][idx] || '#888'
                                }}
                            />
                        ))}
                    </div>
                )}

                {/* Badge - top right */}
                <div className="absolute top-3 right-3">
                    <span className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${badge.color}`}>
                        {badge.label}
                    </span>
                </div>
            </div>

            {/* Content */}
            <div className="p-5 bg-white">
                {/* Product Code */}
                <p className="text-xs text-gray-500 mb-2.5 font-medium tracking-wide">
                    {productCode}
                </p>

                {/* Product Name/Description */}
                <p className="text-sm text-gray-800 font-medium line-clamp-2 leading-relaxed">
                    {product.name}
                </p>
            </div>
        </article>
    );
};

export default ProductCard;
